/**
 * Fabrique de hooks de statistiques.
 *
 * Les trois sections partagent exactement la même mécanique : mêmes paramètres
 * de période, même politique de cache, même traitement du 401. Seuls l'URL et
 * le type de réponse changent — d'où une fabrique plutôt que trois copies qui
 * divergeraient à la première correction.
 */

import { keepPreviousData, useQuery, type UseQueryResult } from '@tanstack/react-query';

import { ApiRequestError } from '../lib/api';
import type { PeriodQuery } from '../types';

const FIVE_MINUTES = 5 * 60 * 1000;

export interface SectionStatsOptions {
  from: string;
  to: string;
  token: string | null;
  topTracksLimit?: number;
  /** Appelé quand l'API répond 401 : le jeton a expiré (30 jours) ou a été révoqué. */
  onUnauthorized?: () => void;
  /**
   * `false` empêche la requête de partir.
   *
   * Indispensable pour les analyses explicatives : les cinq hooks sont appelés
   * à chaque rendu — les règles des hooks l'imposent — mais une seule analyse
   * est affichée. Sans ce drapeau, ouvrir l'onglet déclencherait les cinq
   * requêtes, dont quatre inutiles et toutes coûteuses.
   */
  enabled?: boolean;
}

export function useSectionStats<TStats>(
  /** Préfixe de clé de cache — doit être unique par section. */
  key: string,
  fetcher: (query: PeriodQuery, token: string, signal?: AbortSignal) => Promise<{ stats: TStats }>,
  { from, to, token, topTracksLimit, onUnauthorized, enabled = true }: SectionStatsOptions,
): UseQueryResult<TStats, Error> {
  return useQuery<TStats, Error>({
    queryKey: [key, from, to, topTracksLimit ?? null],

    queryFn: async ({ signal }) => {
      if (!token) throw new ApiRequestError(401, 'Session expiree');
      try {
        const response = await fetcher(
          topTracksLimit !== undefined ? { from, to, topTracksLimit } : { from, to },
          token,
          signal,
        );
        return response.stats;
      } catch (error) {
        if (error instanceof ApiRequestError && error.status === 401) {
          onUnauthorized?.();
        }
        throw error;
      }
    },

    enabled: enabled && Boolean(token),

    // Les donnees ne bougent qu'une fois par nuit, et l'API les met deja en
    // cache 5 minutes : rien ne sert de redemander avant.
    staleTime: FIVE_MINUTES,
    refetchOnWindowFocus: false,

    // Garde le rendu precedent pendant qu'une nouvelle periode charge.
    placeholderData: keepPreviousData,

    retry: (failureCount, error) => {
      if (error instanceof ApiRequestError && error.status >= 400 && error.status < 500) {
        return false;
      }
      return failureCount < 2;
    },
  });
}
